class HeroBanner extends HTMLElement {
  constructor() {
    super();
    this.headerElement = null;
    this.intersectionObserver = null;
    this.headerColor = null;
    this.isActive = false;
    this.scrollHandler = null;
    this.throttleTimer = null;
  }

  connectedCallback() {
    this.headerColor = this.dataset.headerColor;
    if (!this.headerColor) return;

    this.headerElement = document.querySelector("[header-logo]");
    if (!this.headerElement) return;

    this.setupObservers();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler, {
        passive: true,
      });
    }

    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
    }

    if (this.isActive) {
      this.clearHeaderStyle();
    }
  }

  setupObservers() {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateHeaderControl();
        } else {
          this.deactivate();
        }
      });
    }, options);

    this.intersectionObserver.observe(this);

    this.scrollHandler = () => {
      if (this.throttleTimer) {
        clearTimeout(this.throttleTimer);
      }

      this.throttleTimer = setTimeout(() => {
        this.updateHeaderControl();
      }, 16);
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
  }

  updateHeaderControl() {
    if (!this.isInViewport() || !this.overlapsWithHeader()) {
      this.deactivate();
      return;
    }

    const shouldControl = this.shouldControlHeader();

    if (shouldControl && !this.isActive) {
      this.activate();
    } else if (!shouldControl && this.isActive) {
      this.deactivate();
    }
  }

  isInViewport() {
    const bounds = this.getBoundingClientRect();
    return bounds.bottom > 0 && bounds.top < window.innerHeight;
  }

  overlapsWithHeader() {
    const heroBounds = this.getBoundingClientRect();
    const headerBounds = this.headerElement.getBoundingClientRect();

    return !(
      heroBounds.right < headerBounds.left ||
      heroBounds.left > headerBounds.right ||
      heroBounds.bottom < headerBounds.top ||
      heroBounds.top > headerBounds.bottom
    );
  }

  shouldControlHeader() {
    const allHeroBanners = document.querySelectorAll("hero-banner");
    let topMost = null;
    let topMostY = Infinity;

    allHeroBanners.forEach((banner) => {
      if (!banner.headerColor || !banner.overlapsWithHeader()) return;

      const bounds = banner.getBoundingClientRect();
      if (bounds.top < topMostY) {
        topMostY = bounds.top;
        topMost = banner;
      }
    });

    return topMost === this;
  }

  activate() {
    document.querySelectorAll("hero-banner").forEach((banner) => {
      if (banner !== this && banner.isActive) {
        banner.deactivate();
      }
    });

    this.isActive = true;
    this.applyHeaderStyle();
  }

  deactivate() {
    if (this.isActive) {
      this.isActive = false;
      this.clearHeaderStyle();
    }
  }

  applyHeaderStyle() {
    if (this.headerElement && this.headerColor) {
      this.headerElement.style.color = this.headerColor;
    }
  }

  clearHeaderStyle() {
    if (this.headerElement) {
      this.headerElement.style.color = "";
    }
  }
}

customElements.define("hero-banner", HeroBanner);
